import { DatagridConfigurable, DateField, NumberField, RaRecord, TextField } from "react-admin";
import CoreEditButton from "../../../../shared/components/core/CoreEditButton";
import sacopModules from "../../../../shared/utilities/sacopModules";
import CoreShowButton from "../../../../shared/components/core/CoreShowButton";
import { InputAdornment, Box, Typography, Chip } from "@mui/material";

interface MobileGridProps {
    data?: RaRecord[];
}

// Función para determinar el color de la fila basado en el estado y tiempo por etapas
const getRowStyle = (record: RaRecord) => {
    const now = new Date();
    const dateRequestAction = record.dateRequestAction ? new Date(record.dateRequestAction) : null;
    const dateOfFormationOfWorkTeam = record.dateOfFormationOfWorkTeam ? new Date(record.dateOfFormationOfWorkTeam) : null;
    const implementationDate = record.implementationDate ? new Date(record.implementationDate) : null;
    const closeDate = record.closeDate ? new Date(record.closeDate) : null;
    const verificationDate = record.verificationDate ? new Date(record.verificationDate) : null;
    const verificationStatus = record.verificationStatus?.statusName;

    // Si la verificación fue exitosa (Aprobada), color VERDE
    if (verificationStatus === 'Aprobada') {
        return { backgroundColor: '#e8f5e8' }; // Verde suave
    }

    // Si la verificación lleva más de 180 días desde la fecha de cierre, color ROJO
    if (closeDate && !verificationDate) {
        const daysSinceClose = Math.floor((now.getTime() - closeDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceClose > 180) {
            return { backgroundColor: '#ffebee' }; // Rojo suave
        }
    }

    // Si el cierre lleva más de 10 días desde la fecha de implementación, color NARANJA
    if (implementationDate && !closeDate) {
        const daysSinceImplementation = Math.floor((now.getTime() - implementationDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceImplementation > 10) {
            return { backgroundColor: '#fff3e0' }; // Naranja suave
        }
    }

    // Si la implementación lleva más de 10 días desde la fecha de análisis, color AMARILLO
    if (dateOfFormationOfWorkTeam && !implementationDate) {
        const daysSinceAnalysis = Math.floor((now.getTime() - dateOfFormationOfWorkTeam.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceAnalysis > 10) {
            return { backgroundColor: '#fffde7' }; // Amarillo suave
        }
    }

    // Si el análisis lleva más de 10 días desde la fecha de registro, color sugerido: AZUL claro
    if (dateRequestAction && !dateOfFormationOfWorkTeam) {
        const daysSinceRequest = Math.floor((now.getTime() - dateRequestAction.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceRequest > 10) {
            return { backgroundColor: '#e3f2fd' }; // Azul muy suave
        }
    }

    // Default: sin color especial
    return {};
};

const MobileGrid = (props: MobileGridProps) => {

    return (
        <>
            {/* Leyenda de colores */}
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Leyenda de colores:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        label="Análisis > 10 días"
                        size="small"
                        sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                    />
                    <Chip
                        label="Implementación > 10 días"
                        size="small"
                        sx={{ backgroundColor: '#fffde7', color: '#f57f17' }}
                    />
                    <Chip
                        label="Cierre > 10 días"
                        size="small"
                        sx={{ backgroundColor: '#fff3e0', color: '#e65100' }}
                    />
                    <Chip
                        label="Verificación > 180 días"
                        size="small"
                        sx={{ backgroundColor: '#ffebee', color: '#d32f2f' }}
                    />
                    <Chip
                        label="Verificación exitosa"
                        size="small"
                        sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
                    />
                </Box>
            </Box>

            <DatagridConfigurable
            bulkActionButtons={false}
            rowStyle={getRowStyle}
            omit={[
                'responsibleForAnalysis.fullName',
                'responsibleForImplementation.fullName',
                'responsibleForClosing.fullName',
                'responsibleForVerification.fullName',
                'dateOfFormationOfWorkTeam',
                'implementationDate',
                'closeDate',
                'verificationDate',
                'expirationDateAction',
                'valuationAmount',
                'verificationStatus.statusName'
            ]}
        >
            <TextField source="id" label="resources.sacop.fields.id" variant="body1" />
            <TextField source="typeOfGeneratingEntity.nameOfGeneratingEntity" label="resources.sacop.fields.generator" variant="body1" sortable={false} />
            <TextField source="motive.motiveName" label="resources.sacop.fields.motive.motiveName" variant="body1" sortable={false} />
            <TextField source="regulation.regulationName" label="resources.sacop.fields.regulationId" variant="body1" sortable={false} />
            <DateField source="dateRequestAction" label="resources.sacop.fields.dateRequestAction" variant="body1" />
            <DateField source="dateOfFormationOfWorkTeam" label="resources.sacop.fields.analysisDate" variant="body1" />
            <TextField source="responsibleForAnalysis.fullName" label="resources.sacop.fields.responsibleForAnalysisId" variant="body1" sortable={false} />
            <DateField source="implementationDate" label="resources.sacop.fields.implementationDate" variant="body1" />
            <TextField source="responsibleForImplementation.fullName" label="resources.sacop.fields.responsibleForImplementationId" variant="body1" sortable={false} />
            <DateField source="closeDate" label="resources.sacop.fields.closeDate" variant="body1" />
            <TextField source="responsibleForClosing.fullName" label="resources.sacop.fields.responsibleForClosingId" variant="body1" sortable={false} />
            <DateField source="verificationDate" label="resources.sacop.fields.verificationDate" variant="body1" />
            <TextField source="responsibleForVerification.fullName" label="resources.sacop.fields.responsibleForVerificationId" variant="body1" sortable={false} />
            <DateField source="expirationDateAction" label="resources.sacop.fields.expirationDateAction" variant="body1" />
            <NumberField source="valuationAmount" label="resources.sacop.fields.valuationAmount" variant="body1" options={{
                InputProps: {
                    startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                    ),
                },
            }} />
            <TextField source="closingStatus.statusName" label="resources.sacop.fields.actionStatus" variant="body1" sortable={false} />
            <TextField source="verificationStatus.statusName" label="resources.sacop.fields.verificationStatus" variant="body1" sortable={false} />

            <CoreEditButton permission="BACKOFFICE_MASTER_OF_SHARES_U" actionModule={sacopModules.MASTEROFSHARES} />
            <CoreShowButton permission="BACKOFFICE_MASTER_OF_SHARES_L" />
        </DatagridConfigurable>
        </>
    );
}

export default MobileGrid;